// Syft ACP - Core <https://github.com/Syft-Application/syft2acp>
// © Syft Online Limited

import { keyPrefix } from 'syft-acp-core/constants'

// Type-only, so this module adds no runtime dependency on the reducer index and
// cannot create an import cycle with it.
import type { SliceName } from './index'

/**
 * The slices written to localStorage, handed to redux-persist as its whitelist.
 *
 * Persist only what cannot be rebuilt after a reload. None of the below is an
 * API cache: nothing refetches them, or they are read at first paint.
 * Persisting caches is what filled the origin and broke the session write.
 *
 * Anything absent is ephemeral and gets swept by `purgeUnpersistedKeys`, so a
 * new slice needs no entry here. Typed as `SliceName` rather than `string` so a
 * typo, or a slice that gets renamed out from under it, fails the build.
 */
export const persistedSlices: readonly SliceName[] = [
  'auth',
  // Must travel with `auth`. The REHYDRATE case in reducers/auth.ts reads a
  // stored `auth.oauthData` against an empty rehydrated `oauthData` as a logout
  // elsewhere, so persisting `auth` alone logs the user out on every load.
  'oauthData',
  'userSettings',
  'settings',
  'dismissibleAlerts',
  'countries',
  'app',
]

/**
 * Removes the keys ACP wrote for slices it no longer persists.
 *
 * Needed because redux-persist never removes a key it stops owning: it skips
 * the key on write and filters it on read, but never calls removeItem.
 *
 * Works from the slice names rather than from everything under the prefix,
 * because `flex.` is not ours alone. One Host is served from the same origin
 * and its modules write `flex.stsJwt` and `flex.oauthData` through
 * `@indeed/flex-client-auth`, so deleting every unrecognised `flex.` key takes
 * another app's session with it. A name matching a live slice is the only proof
 * available that redux-persist wrote the key.
 *
 * The cost is that a slice deleted in some earlier release keeps its key: the
 * name is gone, so ownership can no longer be shown.
 *
 * @param storage the storage to sweep
 * @param sliceNames the slices the root reducer currently produces
 * @returns the keys that were removed
 */
export const purgeUnpersistedKeys = (
  storage: Pick<Storage, 'getItem' | 'removeItem'>,
  sliceNames: readonly string[],
): string[] => {
  const persisted = new Set<string>(persistedSlices)
  const stale = sliceNames
    .filter(slice => !persisted.has(slice))
    .map(slice => `${keyPrefix}${slice}`)
    .filter(key => storage.getItem(key) !== null)

  stale.forEach(key => storage.removeItem(key))

  return stale
}
